import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/ar-sa";
import { TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { useSelector } from "react-redux";
import { Clock } from "lucide-react";
import { UIFieldShell, useUIField } from "../form";
import messages from "../../../assets/locale/messages";
import { clx } from "../../../utils/Helpers";
import { uiTokens } from "../../../utils/uiTokens";

dayjs.extend(customParseFormat);

const ClockIcon = () => (
  <Clock size={24} color="var(--color-foreground-subtle, #94A3B8)" />
);

const UITimeField = ({
  label,
  optionalLabel,
  required,
  helperText,
  error,
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  inputFormat,
  ampm,
  className,
  size,
  components,
  PopperProps,
  ...props
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared } = messages[lang];
  const fieldId = id || name;
  const field = useUIField({ name, value, error, helperText });
  const adapterLocale = lang === "ar" ? "ar-sa" : "en";
  const pickerValue = field.value
    ? dayjs(field.value, "HH:mm").locale(adapterLocale)
    : null;
  const resolvedInputFormat = inputFormat || "hh:mm A";
  const resolvedPlaceholder = placeholder || "--:-- --";
  const amLabel = shared.am;
  const pmLabel = shared.pm;

  return (
    <UIFieldShell
      id={fieldId}
      label={label}
      optionalLabel={optionalLabel}
      required={required}
      helperText={field.helperText}
      error={field.showError}
    >
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={adapterLocale}
      >
        <DesktopTimePicker
          {...props}
          value={pickerValue?.isValid() ? pickerValue : null}
          ampm={ampm}
          inputFormat={resolvedInputFormat}
          disableMaskedInput
          components={{
            OpenPickerIcon: ClockIcon,
            ...components,
          }}
          PopperProps={{
            ...PopperProps,
            sx: [
              {
                "& .MuiClock-amButton, & .MuiClock-pmButton": {
                  width: uiTokens.space[40],
                  height: uiTokens.space[40],
                  minWidth: uiTokens.space[40],
                  padding: 0,
                  borderRadius: "50%",
                  boxSizing: "border-box",
                },
                "& .MuiClock-amButton:hover, & .MuiClock-pmButton:hover": {
                  backgroundColor: `${uiTokens.color.semantic.primary.default} !important`,
                  color: `${uiTokens.color.semantic.primary.foreground} !important`,
                },
                "& .MuiClock-amButton .MuiTypography-root, & .MuiClock-pmButton .MuiTypography-root":
                  {
                    fontSize: 0,
                    lineHeight: 0,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                "& .MuiClock-amButton .MuiTypography-root::after": {
                  content: `"${amLabel}"`,
                  fontSize: uiTokens.typography.body.xs.fontSize,
                  lineHeight: uiTokens.typography.body.xs.lineHeight,
                  color: "inherit",
                },
                "& .MuiClock-pmButton .MuiTypography-root::after": {
                  content: `"${pmLabel}"`,
                  fontSize: uiTokens.typography.body.xs.fontSize,
                  lineHeight: uiTokens.typography.body.xs.lineHeight,
                  color: "inherit",
                },
              },
              PopperProps?.sx,
            ],
          }}
          onChange={(nextValue) => {
            const next =
              nextValue && dayjs(nextValue).isValid()
                ? dayjs(nextValue).format("HH:mm")
                : "";
            field.setValue(next);
            onChange?.(next, nextValue);
          }}
          onClose={() => field.setTouched(true)}
          renderInput={(params) => (
            <TextField
              {...params}
              id={fieldId}
              name={name}
              error={field.showError}
              helperText={null}
              className={clx(
                "ui-input",
                pickerValue?.isValid() && "ui-input--filled",
                className,
              )}
              size={size}
              onBlur={(event) => {
                field.setTouched(true);
                onBlur?.(event);
                params.inputProps?.onBlur?.(event);
              }}
              inputProps={{
                ...params.inputProps,
                placeholder: resolvedPlaceholder,
              }}
              sx={{
                "& .MuiInputBase-input": {
                  direction: lang === "ar" ? "rtl" : "ltr",
                  textAlign: lang === "ar" ? "right" : "left",
                },
                "& .MuiInputAdornment-root .MuiIconButton-root": {
                  backgroundColor: "transparent",
                  "&:hover": { backgroundColor: "transparent" },
                  "&:focus-visible": { backgroundColor: "transparent" },
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
    </UIFieldShell>
  );
};

UITimeField.propTypes = {
  label: PropTypes.node,
  optionalLabel: PropTypes.node,
  required: PropTypes.bool,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  inputFormat: PropTypes.string,
  ampm: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(["medium", "large"]),
  components: PropTypes.object,
  PopperProps: PropTypes.shape({
    sx: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.array,
      PropTypes.func,
    ]),
  }),
};

UITimeField.defaultProps = {
  label: null,
  optionalLabel: null,
  required: false,
  helperText: null,
  error: undefined,
  id: undefined,
  name: undefined,
  value: undefined,
  onChange: undefined,
  onBlur: undefined,
  placeholder: undefined,
  inputFormat: undefined,
  ampm: true,
  className: undefined,
  size: "medium",
  components: undefined,
  PopperProps: undefined,
};

export default UITimeField;
